import React, {useState, useRef, useEffect} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {FiUser, FiMail, FiPhone, FiBriefcase, FiCalendar, FiCheck, FiArrowRight, FiArrowLeft} from 'react-icons/fi';
import {useWorkshopSchedule} from '../hooks/useWorkshopSchedule';

const WEBHOOK_URL = "https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjcwNTZkMDYzMzA0MzU1MjZiNTUzNjUxMzQi_pc";
const THANK_YOU_URL = "https://thankyoufree.10xaifounder.com/";

const countryCodes = [
  {code: "+91", country: "India", short: "IN", flag: "🇮🇳"},
  {code: "+1", country: "United States", short: "US", flag: "🇺🇸"},
  {code: "+44", country: "United Kingdom", short: "UK", flag: "🇬🇧"},
  {code: "+65", country: "Singapore", short: "SG", flag: "🇸🇬"},
  {code: "+971", country: "United Arab Emirates", short: "AE", flag: "🇦🇪"}
];

// Multi-step form questions - Mixed Script (Tamil Script + English Words)
const qualifyingQuestions = [
  {
    id: 'q1',
    question: 'Business-ல் உங்களுக்கு இருக்கிற பெரிய struggle என்ன?',
    options: [
      "நிறைய Enquiries வருது, but Orders வரல",
      "வர்ற Enquiries-க்கு எல்லாம் Manual-ஆ Reply பண்ணி Tired ஆயிடுச்சு",
      "Price சொன்னதுக்கு அப்புறம் Customer Reply பண்றது இல்ல",
      "Income Consistent-ஆ இல்ல",
      "Tools & Tech பத்தி ஒன்னும் தெரியாது"
    ]
  },
  {
    id: 'q2',
    question: 'இப்போ Enquiries-ஐ எப்படி handle பண்றீங்க?',
    options: [
      'WhatsApp-ல ஒவ்வொருத்தருக்கும் Manual-ஆ Type பண்றேன்',
      'Call பண்ணி பேசுவேன்',
      'Proper System எதுவும் இல்ல',
      'Already Automation use பண்றேன்'
    ]
  },
  {
    id: 'q3',
    question: 'இதுக்கு முன்னாடி Automation Tools use பண்ணி இருக்கீங்களா?',
    options: [
      'இது வரைக்கும் இல்ல',
      'Basic Tools மட்டும் தெரியும்',
      'Try பண்ணேன், but சரியா work ஆகல',
      'Yes, நல்லா தெரியும்'
    ]
  },
  {
    id: 'q4',
    question: 'உங்களுக்காக FREE AI Auto-Reply பண்ணுனா உங்களுக்கு okay-வா?',
    options: [
      'கண்டிப்பா! எனக்கு Time Save ஆகணும்',
      'Okay, but எப்படி work ஆகும்னு தெரியணும்',
      'எனக்கு set ஆகுமானு தெரியல',
      'வேண்டாம், நான் Manual-ஆ பாத்துக்கிறேன்'
    ]
  },
  {
    id: 'q5',
    question: 'நீங்க Business-ஐ எப்படி பாக்குறீங்க?',
    options: [
      "I'm serious. என் Brand-ஐ பெருசு படுத்தணும்",
      "Time pass-க்காக பண்றேன், பெரிய Plans இல்ல"
    ]
  }
];

export default function RegistrationForm({isOpen, onClose, onSuccess}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    phone: '',
    profession: '',
    consent: false,
    q1: '',
    q2: '',
    q3: '',
    q4: '',
    q5: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const firstInputRef = useRef(null);
  const {workshopDetails} = useWorkshopSchedule();

  const totalSteps = 7; // 5 questions + 1 personal info + 1 final

  useEffect(() => {
    if (isOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [isOpen, currentStep]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({...prev, [field]: value}));
    if (formErrors[field]) {
      setFormErrors(prev => ({...prev, [field]: ''}));
    }
  };

  const validateCurrentStep = () => {
    const errors = {};

    if (currentStep < 5) {
      // Validate qualifying questions
      const questionId = `q${currentStep + 1}`;
      if (!formData[questionId]) {
        errors[questionId] = 'Please select an option';
      }
    } else if (currentStep === 5) {
      // Validate personal information
      if (!formData.name.trim()) errors.name = 'Name is required';
      if (!formData.email.trim()) errors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Please enter a valid email';
      if (!formData.phone.trim()) errors.phone = 'Phone number is required';
      else if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, ''))) errors.phone = 'Please enter a valid 10-digit phone number';
      if (!formData.profession.trim()) errors.profession = 'Profession/Role is required';
    } else if (currentStep === 6) {
      // Validate consent
      if (!formData.consent) errors.consent = 'Please agree to the terms';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep < totalSteps - 1) {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateCurrentStep() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const phoneWithCountryCode = `${formData.countryCode}${formData.phone}`;
      const selectedCountry = countryCodes.find(c => c.code === formData.countryCode);

      // Get tracking data
      let trackingData = window.trackingData || {};
      if (Object.keys(trackingData).length === 0) {
        try {
          const storedData = sessionStorage.getItem('trackingData');
          if (storedData) trackingData = JSON.parse(storedData);
        } catch (e) {
          console.warn('Failed to retrieve tracking data:', e);
        }
      }

      // Prepare webhook payload with qualifying questions
      const webhookPayload = {
        // User Information
        name: formData.name,
        email: formData.email,
        phone: phoneWithCountryCode,
        country_name: selectedCountry ? selectedCountry.country : '',
        country_code: formData.countryCode,
        profession: formData.profession,

        // Qualifying Questions
        biggest_struggle: formData.q1,
        follow_up_method: formData.q2,
        automation_experience: formData.q3,
        open_to_automation: formData.q4,
        business_mindset: formData.q5,

        // Webinar Information - ISO Timestamp Format
        webinar_batch: workshopDetails.date.toISOString(), // e.g., "2025-06-22T02:45:00.000Z"
        webinar_date: workshopDetails.formattedDate,
        webinar_time: workshopDetails.timeString,

        // Tracking data (preserved from original)
        campaign_id: trackingData.campaign_id || '',
        campaign_name: trackingData.campaign_name || '',
        adset_id: trackingData.adset_id || '',
        ad_set_id: trackingData.adset_id || '',
        adset_name: trackingData.adset_name || '',
        ad_set_name: trackingData.adset_name || '',
        ad_id: trackingData.ad_id || '',
        ad_name: trackingData.ad_name || '',
        fb_campaign_id: trackingData.fb_campaign_id || '',
        fb_adset_id: trackingData.fb_adset_id || '',
        fb_ad_id: trackingData.fb_ad_id || '',
        utm_source: trackingData.utm_source || '',
        utm_medium: trackingData.utm_medium || '',
        utm_campaign: trackingData.utm_campaign || '',
        utm_term: trackingData.utm_term || '',
        utm_content: trackingData.utm_content || '',
        fbclid: trackingData.fbclid || '',
        gclid: trackingData.gclid || '',
        referrer: trackingData.referrer || '',
        landing_page: trackingData.landing_page || window.location.href,
        user_agent: trackingData.user_agent || navigator.userAgent,
        registration_timestamp: new Date().toISOString(),
        page_load_time: trackingData.page_load_time || '',
        form_source: 'webinar_registration',
        form_version: '2.0_christmas_qualifying'
      };

      console.log('📤 Sending webhook payload:', webhookPayload);

      // Send to webhook
      try {
        const response = await fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(webhookPayload)
        });

        if (response.ok) {
          console.log('✅ Webhook sent successfully');
        } else {
          console.warn('⚠️ Webhook response not OK:', response.status);
        }
      } catch (webhookError) {
        console.error('❌ Webhook submission failed:', webhookError);
      }

      // Track Facebook Pixel
      if (typeof fbq !== 'undefined') {
        fbq('track', 'Lead', {
          content_name: 'Free Sales Automation Webinar Registration - Christmas Special',
          content_category: 'Webinar',
          value: 0,
          currency: 'INR'
        });
      }

      // Success
      onSuccess();
      onClose();

      // Redirect
      setTimeout(() => {
        window.location.href = THANK_YOU_URL;
      }, 1500);

    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderProgressBar = () => (
    <div className="form-progress">
      {Array.from({length: totalSteps}).map((_, index) => (
        <div key={index} className="progress-step">
          <div className={`progress-circle ${index < currentStep ? 'completed' : index === currentStep ? 'active' : ''}`}>
            {index < currentStep ? <FiCheck /> : index + 1}
          </div>
          <span className={`progress-label ${index === currentStep ? 'active' : ''}`}>
            {index < 5 ? `Q${index + 1}` : index === 5 ? 'Info' : 'Done'}
          </span>
        </div>
      ))}
    </div>
  );

  const renderQuestionStep = (questionIndex) => {
    const question = qualifyingQuestions[questionIndex];
    const questionId = `q${questionIndex + 1}`;

    return (
      <motion.div
        key={`question-${questionIndex}`}
        className="form-step"
        initial={{opacity: 0, x: 20}}
        animate={{opacity: 1, x: 0}}
        exit={{opacity: 0, x: -20}}
        transition={{duration: 0.3}}
      >
        <h4 className="step-title">{question.question}</h4>
        <div className="radio-options">
          {question.options.map((option, idx) => (
            <div key={idx} className="radio-card">
              <input
                type="radio"
                id={`${questionId}-${idx}`}
                name={questionId}
                value={option}
                checked={formData[questionId] === option}
                onChange={(e) => handleInputChange(questionId, e.target.value)}
                ref={idx === 0 ? firstInputRef : null}
              />
              <label htmlFor={`${questionId}-${idx}`} className="radio-label">
                <div className="radio-icon"></div>
                <span className="radio-text">{option}</span>
              </label>
            </div>
          ))}
        </div>
        {formErrors[questionId] && (
          <span className="error-message" role="alert">{formErrors[questionId]}</span>
        )}
      </motion.div>
    );
  };

  const renderPersonalInfoStep = () => (
    <motion.div
      key="personal-info"
      className="form-step"
      initial={{opacity: 0, x: 20}}
      animate={{opacity: 1, x: 0}}
      exit={{opacity: 0, x: -20}}
      transition={{duration: 0.3}}
    >
      <h4 className="step-title">Your Contact Information</h4>

      <div className="form-group">
        <label htmlFor="name">
          <FiUser className="input-icon" />
          உங்கள் பெயர் (Full Name) *
        </label>
        <input
          ref={firstInputRef}
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          placeholder="Enter your full name"
          className={formErrors.name ? 'error' : ''}
        />
        {formErrors.name && <span className="error-message">{formErrors.name}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="email">
          <FiMail className="input-icon" />
          Email Address *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          placeholder="Enter your email address"
          className={formErrors.email ? 'error' : ''}
        />
        {formErrors.email && <span className="error-message">{formErrors.email}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="phone">
          <FiPhone className="input-icon" />
          WhatsApp Number *
        </label>
        <div className="phone-input-group">
          <select
            value={formData.countryCode}
            onChange={(e) => handleInputChange('countryCode', e.target.value)}
            className="country-select"
          >
            {countryCodes.map((country) => (
              <option key={country.code} value={country.code}>
                {country.flag} {country.code}
              </option>
            ))}
          </select>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, ''))}
            placeholder="9884899080"
            className={formErrors.phone ? 'error' : ''}
            maxLength="10"
          />
        </div>
        {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="profession">
          <FiBriefcase className="input-icon" />
          Profession/Role *
        </label>
        <select
          id="profession"
          name="profession"
          value={formData.profession}
          onChange={(e) => handleInputChange('profession', e.target.value)}
          className={formErrors.profession ? 'error' : ''}
        >
          <option value="">Select your profession</option>
          <option value="Entrepreneur">Entrepreneur</option>
          <option value="Business Owner">Business Owner</option>
          <option value="Startup Founder">Startup Founder</option>
          <option value="Freelancer">Freelancer</option>
          <option value="Consultant">Consultant</option>
          <option value="Executive">Executive</option>
          <option value="Other">Other</option>
        </select>
        {formErrors.profession && <span className="error-message">{formErrors.profession}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="workshop-batch">
          <FiCalendar className="input-icon" />
          Workshop Batch
        </label>
        <div className="form-display-field">
          {workshopDetails.batchString}
        </div>
      </div>
    </motion.div>
  );

  const renderFinalStep = () => (
    <motion.div
      key="final-step"
      className="form-step"
      initial={{opacity: 0, scale: 0.9}}
      animate={{opacity: 1, scale: 1}}
      exit={{opacity: 0, scale: 0.9}}
      transition={{duration: 0.3}}
    >
      <h4 className="step-title">🎄 Almost Done! 🎆</h4>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            ref={firstInputRef}
            type="checkbox"
            checked={formData.consent}
            onChange={(e) => handleInputChange('consent', e.target.checked)}
            className={formErrors.consent ? 'error' : ''}
          />
          <span>I agree to receive updates about the webinar and understand the terms & conditions *</span>
        </label>
        {formErrors.consent && <span className="error-message">{formErrors.consent}</span>}
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        className="submit-button"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Processing...' : '🎯 Complete Registration - FREE 🚀'}
      </button>

      <p className="form-note">
        பதிவு செய்தவுடன் Webinar Link உங்களுக்கு அனுப்பப்படும்.
        <br />
        <strong>100% FREE • No Credit Card Required</strong>
      </p>
    </motion.div>
  );

  const renderCurrentStep = () => {
    if (currentStep < 5) {
      return renderQuestionStep(currentStep);
    } else if (currentStep === 5) {
      return renderPersonalInfoStep();
    } else {
      return renderFinalStep();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        className="modal-content"
        initial={{opacity: 0, scale: 0.8, y: 20}}
        animate={{opacity: 1, scale: 1, y: 0}}
        exit={{opacity: 0, scale: 0.8, y: 20}}
      >
        <div className="modal-header">
          <h3>🎄 FREE Christmas & New Year Webinar 🎆</h3>
          <button className="close-button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <form className="registration-form" onSubmit={(e) => e.preventDefault()}>
          {renderProgressBar()}

          <AnimatePresence mode="wait">
            {renderCurrentStep()}
          </AnimatePresence>

          {currentStep < 6 && (
            <div className="form-navigation">
              {currentStep > 0 && (
                <button type="button" onClick={handleBack} className="nav-button">
                  <FiArrowLeft /> Back
                </button>
              )}
              <button
                type="button"
                onClick={handleNext}
                className="nav-button primary"
                style={{marginLeft: currentStep === 0 ? 'auto' : '0'}}
              >
                Next <FiArrowRight />
              </button>
            </div>
          )}
        </form>
      </motion.div>
    </div>
  );
}